import Button from 'devextreme-react/button';
import DataGrid, {
  Column,
  ColumnChooser,
  ColumnFixing,
  HeaderFilter,
  Item,
  Paging,
  Scrolling,
  SearchPanel,
  Toolbar,
  type DataGridRef
} from 'devextreme-react/data-grid';
import Popup from 'devextreme-react/popup';
import { buildSchedulerTypePalette, OPS_GRID_TOOLBAR_DESKTOP_ONLY, OPS_SITE_GRID_PROPS, OpsGridStateStoring, OpsGridToolbarItems, onGridFocusedRowEnter, onGridRowDblClick, showConfirmDialog, useResponsivePopupOptions, useShellLayout, type OpsGridToolbarMenuItem } from '@jarvis/platform-ui';
import type { FocusedRowChangedEvent, KeyDownEvent, RowDblClickEvent } from 'devextreme/ui/data_grid';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHasRole } from '../../../auth/useHasRole';
import { useAuthStore } from '../../../auth/useAuthStore';
import { TimezoneBanner } from '../../../components/TimezoneBanner';
import { cancelTimeOff, listCalendarTargets, listTimeOff, purgeTimeOff, updateTimeOff, type CalendarTarget, type TimeOffEntry } from '../../../api/timeOff';
import { showToast } from '../../../notifications/toast';
import { useThemeStore } from '../../../theme/useThemeStore';
import { formatAllDayUtcDate, formatLocalDateTime, formatLocalRange } from '../../../utils/timezone';
import { TimeOffEntryDetailContent } from './components/TimeOffEntryDetailContent';
import { TimeOffEntryEditContent } from './components/TimeOffEntryEditContent';
import { TimeOffSyncStatusBanner } from './components/TimeOffSyncStatusBanner';
import { isTimeOffEntryEnded, shouldShowTimeOffEntryInGrid } from './timeOffEntriesGridFilters';
import { canMutateTimeOffEntry, getTimeOffEntrySyncState } from './timeOffEntryMutability';
import { nextAllDayEnd, toApiIso } from './timeOffDateUtils';
import { confirmNoSameDayTimeOffConflict } from './timeOffSameDayWarning';
import {
  timeOffEntryToEditDraft,
  timeOffEntryToViewModel,
  type TimeOffEntryEditDraft
} from './timeOffEntryTypes';
const GRID_STATE_KEY_PREFIX = 'webopsui:time-off-entries-grid:v6';

const TIME_OFF_ENTRIES_GRID_COLUMNS = [
  'employeeDisplayName',
  'startDateText',
  'endDateText',
  'startUtc',
  'endUtc',
  'syncState',
  'updatedAt',
  'notes',
  'employeeUpn',
  'identityStatus',
  'type',
  'status',
  'whenText',
  'allDayText',
  'isAllDay',
  'calendarsText',
  'lastSyncAt'
] as const;

type TimeOffGridRow = TimeOffEntry & {
  identityStatus: string;
  whenText: string;
  startDateText: string;
  endDateText: string;
  allDayText: string;
  calendarsText: string;
  lastSyncAt: Date | null;
  syncState: string;
};

function gridStateStorageKey(username: string | null): string {
  const userKey = (username ?? 'anonymous').trim().toLowerCase().replace(/[^a-z0-9@._-]+/g, '-');
  return `${GRID_STATE_KEY_PREFIX}:${userKey}`;
}

function getLastSyncAt(entry: TimeOffEntry): Date | null {
  const links = entry.calendarLinks ?? [];
  if (links.length === 0) return null;
  const last = links
    .filter((link) => !!link.lastSyncedAt)
    .map((link) => new Date(link.lastSyncedAt!).getTime())
    .sort((a, b) => b - a)[0];
  return last ? new Date(last) : null;
}

function formatGridStartDate(row: TimeOffGridRow): string {
  if (!row.startUtc) return '';
  return row.isAllDay ? formatAllDayUtcDate(row.startUtc) : formatLocalDateTime(row.startUtc);
}

function formatGridEndDate(row: TimeOffGridRow): string {
  if (!row.endUtc) return '';
  if (!row.isAllDay) return formatLocalDateTime(row.endUtc);
  const endDate = new Date(row.endUtc);
  const inclusiveEnd = new Date(endDate.getTime() - 24 * 60 * 60 * 1000);
  const startDate = new Date(row.startUtc);
  const useEnd = inclusiveEnd >= startDate ? inclusiveEnd : endDate;
  return formatAllDayUtcDate(useEnd);
}

function toGridRow(entry: TimeOffEntry): TimeOffGridRow {
  const lastSyncAt = getLastSyncAt(entry);
  const row: TimeOffGridRow = {
    ...entry,
    identityStatus: entry.needsEmployeeIdentity ? 'Needs employee' : '',
    whenText: formatLocalRange(entry.startUtc, entry.endUtc, entry.isAllDay),
    startDateText: '',
    endDateText: '',
    allDayText: entry.isAllDay ? 'Yes' : 'No',
    calendarsText: (entry.calendarLinks ?? []).map((link) => link.calendarTargetDisplayName).join(', '),
    lastSyncAt,
    syncState: getTimeOffEntrySyncState(entry)
  };
  row.startDateText = formatGridStartDate(row);
  row.endDateText = formatGridEndDate(row);
  return row;
}

export function TimeOffEntriesPage() {
  const { hasAnyHrRole, isItSysAdmin } = useHasRole();
  const itSysAdmin = isItSysAdmin();
  const canEditTimeOff = hasAnyHrRole();
  const canCancelTimeOff = canEditTimeOff;
  const canDeleteTimeOff = canEditTimeOff;
  const username = useAuthStore((s) => s.username);
  const themeFamily = useThemeStore((s) => s.family);
  const themeMode = useThemeStore((s) => s.mode);
  const { isNarrow } = useShellLayout();
  const gridRef = useRef<DataGridRef>(null);
  const [entries, setEntries] = useState<TimeOffEntry[]>([]);
  const [hrCalendarTargets, setHrCalendarTargets] = useState<CalendarTarget[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [showCancelled, setShowCancelled] = useState(false);
  const [showPast, setShowPast] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState<TimeOffEntry | null>(null);
  const [detailMode, setDetailMode] = useState<'view' | 'edit'>('view');
  const [editDraft, setEditDraft] = useState<TimeOffEntryEditDraft | null>(null);
  const [popupError, setPopupError] = useState<string | null>(null);
  const [calendarPickerKey, setCalendarPickerKey] = useState(0);
  const [employeePickerKey, setEmployeePickerKey] = useState(0);
  const [actionBusy, setActionBusy] = useState<'idle' | 'save' | 'cancel' | 'delete'>('idle');
  const [focusedRow, setFocusedRow] = useState<TimeOffGridRow | null>(null);

  const typeColors = useMemo(
    () => Object.values(buildSchedulerTypePalette()),
    [themeFamily, themeMode]
  );
  const popupOptions = useResponsivePopupOptions({
    width: detailMode === 'edit' ? 620 : 560,
    height: 'auto'
  });

  const gridStateKey = useMemo(() => gridStateStorageKey(username), [username]);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [list, targets] = await Promise.all([listTimeOff({ take: 1000 }), listCalendarTargets()]);
      setEntries(list);
      setHrCalendarTargets(targets.filter((t) => t.isEnabled));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load time off.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    if (error) showToast(error, 'error');
  }, [error]);

  useEffect(() => {
    if (!selectedEntry) return;
    setDetailMode('view');
    setEditDraft(null);
    setPopupError(null);
    setCalendarPickerKey((key) => key + 1);
    setEmployeePickerKey((key) => key + 1);
  }, [selectedEntry?.id]);

  const resetGridLayout = useCallback(() => {
    gridRef.current?.instance()?.state(null);
    try {
      localStorage.removeItem(gridStateKey);
    } catch {
      /* localStorage may be unavailable */
    }
    showToast(
      'Grid layout reset to defaults. Column visibility, order, widths, and filters will reload on refresh.',
      'info'
    );
  }, [gridStateKey]);

  const visibleEntries = useMemo(
    () =>
      entries.filter((entry) =>
        shouldShowTimeOffEntryInGrid(entry, { showCancelled, showPast })
      ),
    [entries, showCancelled, showPast]
  );

  const gridRows = useMemo(() => visibleEntries.map(toGridRow), [visibleEntries]);

  const noDataText = useMemo(() => {
    if (loading) return 'Loading…';
    if (entries.length > 0 && gridRows.length === 0) {
      return 'No entries match the current filters. Try enabling Show past PTO or Show cancelled.';
    }
    return 'No data';
  }, [entries.length, gridRows.length, loading]);

  const focusedMutable = focusedRow ? canMutateTimeOffEntry(focusedRow) : false;
  const canViewFocused = focusedRow !== null;
  const canEditFocused = !!(focusedRow && canEditTimeOff && focusedMutable);
  const canCancelFocused = !!(focusedRow && canCancelTimeOff && focusedMutable && focusedRow.status !== 'Cancelled');
  const canDeleteFocused = !!(focusedRow && canDeleteTimeOff);

  function openEntryPopup(entry: TimeOffEntry) {
    setSelectedEntry(entry);
    setDetailMode('view');
    setEditDraft(null);
    setPopupError(null);
  }

  function openEntryPopupFromGrid(row: TimeOffGridRow) {
    openEntryPopup(row);
  }

  function openEditForEntry(entry: TimeOffEntry) {
    if (!canEditTimeOff || !canMutateTimeOffEntry(entry)) return;
    setSelectedEntry(entry);
    setPopupError(null);
    setEmployeePickerKey((key) => key + 1);
    setEditDraft(timeOffEntryToEditDraft(entry));
    setDetailMode('edit');
  }

  function beginEditFromPopup() {
    if (!canEditTimeOff || !selectedEntry || !canMutateTimeOffEntry(selectedEntry)) return;
    setPopupError(null);
    setEmployeePickerKey((key) => key + 1);
    setEditDraft(timeOffEntryToEditDraft(selectedEntry));
    setDetailMode('edit');
  }

  async function submitEditFromPopup() {
    if (!editDraft || !selectedEntry) return;
    if (editDraft.calendarTargetIds.length === 0) {
      setPopupError('Select at least one calendar.');
      return;
    }
    const proceedSameDay = await confirmNoSameDayTimeOffConflict({
      employeeUpn: editDraft.employeeUpn.trim(),
      excludeEntryId: selectedEntry.id,
      drafts: [
        {
          startUtc: toApiIso(editDraft.startLocal, editDraft.isAllDay),
          endUtc: toApiIso(
            editDraft.isAllDay ? nextAllDayEnd(editDraft.startLocal) : editDraft.endLocal,
            editDraft.isAllDay
          ),
          isAllDay: editDraft.isAllDay,
          status: editDraft.status
        }
      ]
    });
    if (!proceedSameDay) return;
    setActionBusy('save');
    setPopupError(null);
    try {
      const saved = await updateTimeOff(selectedEntry.id, {
        employeeUpn: editDraft.employeeUpn.trim(),
        employeeDisplayName: editDraft.employeeDisplayName,
        type: editDraft.type,
        startUtc: toApiIso(editDraft.startLocal, editDraft.isAllDay),
        endUtc: toApiIso(editDraft.isAllDay ? nextAllDayEnd(editDraft.startLocal) : editDraft.endLocal, editDraft.isAllDay),
        isAllDay: editDraft.isAllDay,
        notes: editDraft.notes,
        status: editDraft.status,
        calendarTargetIds: editDraft.calendarTargetIds,
        rowVersion: selectedEntry.rowVersion
      });
      setEntries((prev) => prev.map((row) => (row.id === saved.id ? saved : row)));
      setSelectedEntry(saved);
      setDetailMode('view');
      setEditDraft(null);
      showToast('Time off entry updated.', 'success');
    } catch (err) {
      setPopupError(err instanceof Error ? err.message : 'Save failed.');
    } finally {
      setActionBusy('idle');
    }
  }

  async function confirmCancelFromPopup() {
    if (!selectedEntry) return;
    const confirmed = await showConfirmDialog({
      title: 'Cancel time off',
      message:
        'Cancel this time off entry? It will be removed from calendar views and deleted from Outlook on the next sync.',
      confirmText: 'Cancel entry',
      cancelText: 'Keep',
      danger: true
    });
    if (!confirmed) return;
    setActionBusy('cancel');
    setPopupError(null);
    try {
      await cancelTimeOff(selectedEntry.id);
      setSelectedEntry(null);
      setDetailMode('view');
      setEditDraft(null);
      showToast(
        'Time off entry cancelled. It is hidden from the default grid unless you enable Show cancelled.',
        'success'
      );
      await refresh();
    } catch (err) {
      setPopupError(err instanceof Error ? err.message : 'Cancel failed.');
    } finally {
      setActionBusy('idle');
    }
  }

  function closePopup() {
    setSelectedEntry(null);
    setDetailMode('view');
    setEditDraft(null);
    setPopupError(null);
  }

  async function confirmDeleteFromPopup() {
    if (!selectedEntry) return;
    const confirmed = await showConfirmDialog({
      title: 'Delete time off',
      message:
        'Permanently delete this time off? It will be removed from Outlook now and from this site. This cannot be undone.',
      confirmText: 'Delete',
      cancelText: 'Keep',
      danger: true
    });
    if (!confirmed) return;
    setActionBusy('delete');
    setPopupError(null);
    try {
      await purgeTimeOff(selectedEntry.id);
      closePopup();
      showToast('Time off entry deleted.', 'success');
      await refresh();
    } catch (err) {
      setPopupError(err instanceof Error ? err.message : 'Delete failed.');
    } finally {
      setActionBusy('idle');
    }
  }

  async function handleDeleteEntry(entry: TimeOffEntry) {
    const confirmed = await showConfirmDialog({
      title: 'Delete time off',
      message:
        'Permanently delete this time off? It will be removed from Outlook now and from this site. This cannot be undone.',
      confirmText: 'Delete',
      cancelText: 'Keep',
      danger: true
    });
    if (!confirmed) return;
    try {
      await purgeTimeOff(entry.id);
      if (selectedEntry?.id === entry.id) {
        closePopup();
      }
      showToast('Time off entry deleted.', 'success');
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delete failed.');
    }
  }

  async function handleCancelEntry(entry: TimeOffEntry) {
    const confirmed = await showConfirmDialog({
      title: 'Cancel time off',
      message:
        'Cancel this time off entry? It will be removed from calendar views and deleted from Outlook on the next sync.',
      confirmText: 'Cancel entry',
      cancelText: 'Keep',
      danger: true
    });
    if (!confirmed) return;
    try {
      await cancelTimeOff(entry.id);
      if (selectedEntry?.id === entry.id) {
        closePopup();
      }
      showToast(
        'Time off entry cancelled. It is hidden from the default grid unless you enable Show cancelled.',
        'success'
      );
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Cancel failed.');
    }
  }

  function renderEntryPopup() {
    if (!selectedEntry) return null;

    const entryMutable = canMutateTimeOffEntry(selectedEntry);

    if (detailMode === 'edit' && editDraft && canEditTimeOff && entryMutable) {
      return (
        <TimeOffEntryEditContent
          draft={editDraft}
          onDraftChange={(updater) => setEditDraft((prev) => (prev ? updater(prev) : prev))}
          error={popupError}
          busy={actionBusy === 'save'}
          itSysAdmin={itSysAdmin}
          hrCalendarTargets={hrCalendarTargets}
          calendarPickerKey={calendarPickerKey}
          employeePickerKey={`emp-edit-${selectedEntry.id}-${employeePickerKey}`}
          onDiscard={() => {
            setPopupError(null);
            setDetailMode('view');
            setEditDraft(null);
          }}
          onClose={closePopup}
          onSave={() => void submitEditFromPopup()}
        />
      );
    }

    return (
      <TimeOffEntryDetailContent
        entry={timeOffEntryToViewModel(selectedEntry)}
        typeColors={typeColors}
        busyCancel={actionBusy === 'cancel'}
        busyDelete={actionBusy === 'delete'}
        onCancel={canCancelTimeOff && entryMutable ? () => void confirmCancelFromPopup() : undefined}
        onDelete={canDeleteTimeOff ? () => void confirmDeleteFromPopup() : undefined}
        onEdit={canEditTimeOff && entryMutable ? beginEditFromPopup : undefined}
        onClose={closePopup}
      />
    );
  }

  const gridExtraMenuItems = useMemo((): OpsGridToolbarMenuItem[] => {
    const items: OpsGridToolbarMenuItem[] = [
      {
        id: 'toggle-cancelled',
        text: showCancelled ? 'Hide cancelled' : 'Show cancelled',
        icon: 'check',
        onClick: () => setShowCancelled((value) => !value)
      },
      {
        id: 'toggle-past',
        text: showPast ? 'Hide past PTO' : 'Show past PTO',
        icon: 'clock',
        onClick: () => setShowPast((value) => !value)
      },
      {
        id: 'view-entry',
        text: 'View details',
        icon: 'eyeopen',
        disabled: !canViewFocused,
        onClick: () => {
          if (focusedRow) openEntryPopup(focusedRow);
        }
      },
      {
        id: 'edit-entry',
        text: 'Edit time off',
        icon: 'edit',
        disabled: !canEditFocused,
        onClick: () => {
          if (focusedRow) openEditForEntry(focusedRow);
        }
      },
      {
        id: 'cancel-entry',
        text: 'Cancel PTO',
        icon: 'clear',
        disabled: !canCancelFocused,
        onClick: () => {
          if (focusedRow) void handleCancelEntry(focusedRow);
        }
      },
      {
        id: 'delete-entry',
        text: 'Delete PTO',
        icon: 'trash',
        disabled: !canDeleteFocused,
        onClick: () => {
          if (focusedRow) void handleDeleteEntry(focusedRow);
        }
      }
    ];
    return items;
  }, [
    showCancelled,
    showPast,
    canViewFocused,
    canEditFocused,
    canCancelFocused,
    canDeleteFocused,
    focusedRow
  ]);

  return (
    <div className="ops-fill-page ops-fill-page--bare">
      {isNarrow ? (
        <div className="ops-fill-page__alerts time-off-entries-mobile-alerts">
          <TimeOffSyncStatusBanner entries={entries} variant="alert" onlyTone="danger" />
        </div>
      ) : null}
      <div className="ops-fill-page__grid-host time-off-entries-grid">
        <DataGrid
          ref={gridRef}
          dataSource={gridRows}
          keyExpr="id"
          noDataText={noDataText}
          height="100%"
          width="100%"
          showBorders
          columnHidingEnabled
          allowColumnResizing
          allowColumnReordering
          columnResizingMode="widget"
          {...OPS_SITE_GRID_PROPS}
          onFocusedRowChanged={(e: FocusedRowChangedEvent<TimeOffGridRow, string>) => {
            setFocusedRow((e.row?.data as TimeOffGridRow | undefined) ?? null);
          }}
          onRowDblClick={(e) =>
            onGridRowDblClick(
              e as RowDblClickEvent<TimeOffGridRow, string | number>,
              openEntryPopupFromGrid
            )
          }
          onKeyDown={(e) =>
            onGridFocusedRowEnter(
              e as KeyDownEvent<TimeOffGridRow, string | number>,
              openEntryPopupFromGrid
            )
          }
          onRowPrepared={(e) => {
            if (e.rowType !== 'data' || !e.rowElement) return;
            const row = e.data as TimeOffGridRow | undefined;
            if (row?.status === 'Cancelled') {
              e.rowElement.classList.add('time-off-entries-grid__row--cancelled');
            } else if (row && isTimeOffEntryEnded(row)) {
              e.rowElement.classList.add('time-off-entries-grid__row--past');
            }
          }}
        >
          <OpsGridStateStoring
            storageKey={gridStateKey}
            schemaVersion={1}
            columns={TIME_OFF_ENTRIES_GRID_COLUMNS}
          />
          <ColumnFixing enabled />
          <ColumnChooser enabled mode="select" allowSearch title="Choose columns" />
          <Scrolling mode="standard" scrollByThumb showScrollbar="onScroll" useNative />
          <Toolbar>
            {!isNarrow ? (
              <>
                <Item
                  location="before"
                  cssClass={OPS_GRID_TOOLBAR_DESKTOP_ONLY}
                  widget="dxCheckBox"
                  options={{
                    text: 'Show cancelled',
                    value: showCancelled,
                    onValueChanged: (e: { value?: boolean }) => setShowCancelled(!!e.value)
                  }}
                />
                <Item
                  location="before"
                  cssClass={OPS_GRID_TOOLBAR_DESKTOP_ONLY}
                  widget="dxCheckBox"
                  options={{
                    text: 'Show past PTO',
                    value: showPast,
                    onValueChanged: (e: { value?: boolean }) => setShowPast(!!e.value)
                  }}
                />
                <Item location="before" cssClass={OPS_GRID_TOOLBAR_DESKTOP_ONLY}>
                  <div className="time-off-page-status-banners">
                    <TimezoneBanner prefix="Local time" className="time-off-page-status-banners__pill" />
                    <TimeOffSyncStatusBanner entries={entries} compact />
                  </div>
                </Item>
                <Item location="after" cssClass={OPS_GRID_TOOLBAR_DESKTOP_ONLY}>
                  <div className="time-off-entries-grid__toolbar-actions">
                    <Button
                      icon="eyeopen"
                      stylingMode="outlined"
                      hint="View details"
                      disabled={!canViewFocused}
                      elementAttr={{ 'aria-label': 'View details' }}
                      onClick={() => focusedRow && openEntryPopup(focusedRow)}
                    />
                    <Button
                      icon="edit"
                      stylingMode="outlined"
                      hint="Edit time off"
                      disabled={!canEditFocused}
                      elementAttr={{ 'aria-label': 'Edit time off' }}
                      onClick={() => focusedRow && openEditForEntry(focusedRow)}
                    />
                    <span className="time-off-entries-grid__toolbar-separator" aria-hidden>
                      |
                    </span>
                    <Button
                      icon="clear"
                      stylingMode="outlined"
                      hint="Cancel PTO"
                      disabled={!canCancelFocused}
                      elementAttr={{ 'aria-label': 'Cancel PTO' }}
                      onClick={() => focusedRow && void handleCancelEntry(focusedRow)}
                    />
                    <Button
                      icon="trash"
                      stylingMode="outlined"
                      type="danger"
                      hint="Delete PTO"
                      disabled={!canDeleteFocused}
                      elementAttr={{ 'aria-label': 'Delete PTO' }}
                      onClick={() => focusedRow && void handleDeleteEntry(focusedRow)}
                    />
                    <span className="time-off-entries-grid__toolbar-separator" aria-hidden>
                      |
                    </span>
                  </div>
                </Item>
              </>
            ) : null}
            <OpsGridToolbarItems
              gridRef={gridRef}
              loading={loading}
              onRefresh={() => void refresh()}
              refreshHint="Refresh entries"
              extraMenuItems={gridExtraMenuItems}
              export={{ fileName: 'time-off-export', worksheetName: 'Time off', hint: 'Export entries' }}
              resetLayout={{ onReset: resetGridLayout }}
            />
            <Item name="searchPanel" location="after" />
          </Toolbar>
          <SearchPanel visible width={240} />
          <HeaderFilter visible />
          <Paging defaultPageSize={25} />
          <Column
            name="employeeDisplayName"
            dataField="employeeDisplayName"
            caption="Display Name"
            minWidth={150}
            cellRender={(c) => {
              const row = c.data as TimeOffGridRow | undefined;
              const cancelled = row?.status === 'Cancelled';
              const past = !!row && !cancelled && isTimeOffEntryEnded(row);
              return (
                <span className={cancelled ? 'time-off-entries-grid__name--cancelled' : past ? 'time-off-entries-grid__name--past' : undefined}>
                  {cancelled ? (
                    <span className="time-off-entries-grid__cancelled-dot" aria-hidden />
                  ) : null}
                  {String(c.value ?? row?.employeeUpn ?? '')}
                </span>
              );
            }}
          />
          <Column name="startUtc" dataField="startUtc" visible={false} dataType="datetime" />
          <Column name="endUtc" dataField="endUtc" visible={false} dataType="datetime" />
          <Column
            name="startDateText"
            dataField="startDateText"
            caption="Start Date"
            minWidth={150}
            calculateSortValue="startUtc"
            calculateFilterExpression={(filterValue, selectedFilterOperation) => [
              'startUtc',
              selectedFilterOperation ?? 'contains',
              filterValue
            ]}
          />
          <Column
            name="endDateText"
            dataField="endDateText"
            caption="End Date"
            minWidth={150}
            calculateSortValue="endUtc"
            calculateFilterExpression={(filterValue, selectedFilterOperation) => [
              'endUtc',
              selectedFilterOperation ?? 'contains',
              filterValue
            ]}
          />
          <Column dataField="syncState" caption="Sync State" width={110} />
          <Column
            dataField="updatedAt"
            caption="Updated"
            width={150}
            dataType="datetime"
            format="MMM d, yyyy h:mm a"
          />
          <Column dataField="notes" caption="Note" minWidth={160} />
          <Column dataField="employeeUpn" caption="Email" minWidth={200} visible={false} />
          <Column dataField="identityStatus" caption="Identity" width={130} visible={false} />
          <Column dataField="type" caption="Type" width={110} visible={false} />
          <Column
            dataField="status"
            caption="Status"
            width={120}
            visible={false}
            cellRender={(c) => {
              const status = String(c.value ?? '');
              if (status === 'Cancelled') {
                return <span className="time-off-entries-grid__status time-off-entries-grid__status--cancelled">{status}</span>;
              }
              return status;
            }}
          />
          <Column
            dataField="whenText"
            caption="When"
            calculateSortValue="startUtc"
            minWidth={200}
            visible={false}
          />
          <Column
            dataField="allDayText"
            caption="All Day"
            width={88}
            alignment="center"
            calculateSortValue="isAllDay"
            visible={false}
          />
          <Column dataField="isAllDay" visible={false} />
          <Column dataField="calendarsText" caption="Calendars" minWidth={120} visible={false} />
          <Column
            dataField="lastSyncAt"
            caption="Last Sync"
            visible={false}
            dataType="datetime"
            calculateDisplayValue={(row: TimeOffGridRow) => (row.lastSyncAt ? formatLocalDateTime(row.lastSyncAt) : '')}
          />
        </DataGrid>
      </div>

      <Popup
        visible={selectedEntry !== null}
        onHiding={closePopup}
        title={detailMode === 'edit' && canEditTimeOff ? 'Edit time off' : 'Time off details'}
        showCloseButton
        dragEnabled
        width={popupOptions.width}
        height={popupOptions.height}
        fullScreen={popupOptions.fullScreen}
        deferRendering={false}
        shading
        contentRender={() => renderEntryPopup()}
      />
    </div>
  );
}
